/**
 * Sidebar Component
 * Main navigation sidebar for the admin panel
 * Displays panel selection and navigation items
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../store/AdminContext';
import { useAuth } from '../../store/AuthContext';
import { panels, PanelType } from '../../store/PanelConfig';
import { apiService } from '../../services/apiService';
import ConfirmModal from '../modals/ConfirmModal';
import './Sidebar.css';

const LogoutButton: React.FC = () => {
  const { logout } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      <div className="nav-item nav-item-logout" onClick={() => setShowConfirm(true)}>
        <span className="icon">🚪</span>
        <span>Logout</span>
      </div>
      {showConfirm && (
        <ConfirmModal
          isOpen={showConfirm}
          title="Confirm Logout"
          message="Are you sure you want to log out?"
          confirmText="Logout"
          cancelText="Cancel"
          confirmButtonClass="danger"
          onConfirm={handleLogout}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  );
};

const UserBox: React.FC = () => {
  const { admin } = useAuth();

  if (!admin) {
    return null;
  }

  const initials = admin.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const getRoleDisplayName = (role: string): string => {
    const roleMap: Record<string, string> = {
      master: 'Master Admin',
      risk: 'Risk & AML Admin',
      finance: 'Finance Admin',
      support: 'Support Admin',
      compliance: 'Compliance Admin',
      content: 'Content Admin',
    };
    return roleMap[role] || role;
  };

  return (
    <div className="user-box">
      <div className="avatar" style={{ background: 'linear-gradient(135deg, var(--purple), var(--blue))' }}>
        {initials}
      </div>
      <div className="user-info">
        <div className="user-name">{admin.name}</div>
        <div className="user-role">{getRoleDisplayName(admin.role)}</div>
      </div>
    </div>
  );
};

const Sidebar: React.FC = () => {
  const { currentPanel, currentPage, setCurrentPanel, setCurrentPage, isSidebarOpen } = useAdmin();
  const { admin } = useAuth();
  
  // Map admin roles to their panel type
  const roleToPanelMap: Record<string, PanelType> = {
    master: 'master',
    risk: 'risk',
    finance: 'finance',
    support: 'support',
    compliance: 'compliance',
    content: 'content',
  };
  
  // Always use the panel that matches the admin's role
  // Non-master admins should only see their own panel's sidebar
  const effectivePanel: PanelType = admin?.role 
    ? (admin.role === 'master' ? currentPanel : (roleToPanelMap[admin.role] || 'master'))
    : currentPanel;
  
  const panel = panels[effectivePanel];
  const [pendingApprovalCount, setPendingApprovalCount] = useState<number>(0);
  const [predictionsOverviewCount, setPredictionsOverviewCount] = useState<number>(0);

  // Get last opened timestamp for a page
  const getLastOpenedTime = (pageId: string): Date | null => {
    const stored = localStorage.getItem(`last_opened_${pageId}`);
    return stored ? new Date(stored) : null;
  };

  // Set last opened timestamp for a page
  const setLastOpenedTime = (pageId: string) => {
    localStorage.setItem(`last_opened_${pageId}`, new Date().toISOString());
  };

  const handlePanelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentPanel(e.target.value as PanelType);
  };

  const handleNavClick = (pageId: string) => {
    if (pageId) {
      setCurrentPage(pageId);
      // Track when tab is opened and reset count
      if (pageId === 'pending-approval') {
        setLastOpenedTime('pending-approval');
        setPendingApprovalCount(0);
      } else if (pageId === 'predictions-overview') {
        setLastOpenedTime('predictions-overview');
        setPredictionsOverviewCount(0);
      }
    }
  };

  // Fetch new markets count since last opened
  useEffect(() => {
    const fetchNewMarketsCount = async () => {
      if (admin?.role === 'master' || admin?.role === 'content') {
        try {
          // Get all markets
          const allMarketsResult = await apiService.getAllMarkets();
          const allMarkets = allMarketsResult?.markets || [];
          
          // Calculate new markets for pending-approval
          const pendingLastOpened = getLastOpenedTime('pending-approval');
          if (pendingLastOpened) {
            const newPendingMarkets = allMarkets.filter(m => {
              if (m.status !== 'pending') return false;
              const createdDate = new Date(m.created_at);
              return createdDate > pendingLastOpened;
            });
            setPendingApprovalCount(newPendingMarkets.length);
          } else {
            // If never opened, show all pending markets
            const pendingMarkets = allMarkets.filter(m => m.status === 'pending');
            setPendingApprovalCount(pendingMarkets.length);
          }

          // Calculate new markets for predictions-overview
          const predictionsLastOpened = getLastOpenedTime('predictions-overview');
          if (predictionsLastOpened) {
            const newMarkets = allMarkets.filter(m => {
              const createdDate = new Date(m.created_at);
              return createdDate > predictionsLastOpened;
            });
            setPredictionsOverviewCount(newMarkets.length);
          } else {
            // If never opened, show all markets
            setPredictionsOverviewCount(allMarkets.length);
          }
        } catch (error) {
          console.error('Failed to fetch new markets count:', error);
        }
      }
    };

    fetchNewMarketsCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchNewMarketsCount, 30000);
    return () => clearInterval(interval);
  }, [admin?.role]);

  // Only master admin can switch between panels
  const isMasterAdmin = admin?.role === 'master';

  if (!isSidebarOpen) {
    return null;
  }

  return (
    <div className="sidebar">
      <div className="logo">
        <div className="logo-icon">VS</div>
        <div className="logo-text">
          VERSUS
          <span>Admin Panel v5.0</span>
        </div>
      </div>

      {isMasterAdmin && (
        <select
          className="panel-select"
          value={effectivePanel}
          onChange={handlePanelChange}
        >
          {Object.entries(panels).map(([key, p]) => {
            // Get icon based on panel type to match HTML format
            const icons: Record<string, string> = {
              master: '🔐',
              content: '📝',
              risk: '🛡️',
              finance: '💰',
              support: '🎧',
              compliance: '📋',
            };
            const icon = icons[key] || '';
            return (
              <option key={key} value={key}>
                {icon} {p.name}
              </option>
            );
          })}
        </select>
      )}

      <div className="nav">
        {panel.nav.map((item, index) => {
          if (item.type === 'label') {
            return (
              <div key={index} className="nav-label">
                {item.label}
              </div>
            );
          }

          // Show dynamic badge for pending-approval and predictions-overview
          let badge = item.badge;
          if (item.id === 'pending-approval' && pendingApprovalCount > 0) {
            badge = pendingApprovalCount;
          } else if (item.id === 'predictions-overview' && predictionsOverviewCount > 0) {
            badge = predictionsOverviewCount;
          }

          return (
            <div
              key={item.id || index}
              className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
              onClick={() => handleNavClick(item.id || '')}
            >
              {item.icon && <span className="icon">{item.icon}</span>}
              {item.label && <span>{item.label}</span>}
              {badge && <span className="badge">{badge}</span>}
            </div>
          );
        })}
        
        {/* Logout button at bottom of navigation */}
        <LogoutButton />
      </div>

      <UserBox />
    </div>
  );
};

export default Sidebar;

